import React, { Fragment, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, CircularProgress, Grid, IconButton, InputAdornment, TextField, Typography } from '@mui/material';

import Styles from '../../theme/commonCss/Styles';
import FormModal from '../../common/formModal';
import { userPasswordActions } from '../../store/reducers/profile';
import { addPasswordUISchema } from './form';

import { ReactComponent as CheckIcon } from '../../assets/icons/main/check-icon.svg';
import { ReactComponent as CircleSmallIcon } from '../../assets/icons/main/circle-small-icon.svg';
import { ReactComponent as EyeIcon } from '../../assets/icons/main/eye-icon.svg';
import { ReactComponent as EyeOffIcon } from '../../assets/icons/main/eye-off-icon.svg';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useOutletContext } from 'react-router-dom';
import { get } from 'lodash';
import {palette} from '../../theme/palette';

const ChangePasswordStyles = {
  defaultStyling: {
    '&.Mui-focused': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderWidth: '1px',
      },
    },
  },
  modalWrapper: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
  gridContainer: {
    marginBottom: '20px',
    width: '100%',
  },
  loadingLayer: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
    cursor: 'not-allowed',
    userSelect: 'none',
  },
  pwdHeading: { fontSize: '1rem', mb: 1 },
  matchCasesWrapper: { mt: '4px', display: 'flex', alignItems: 'center' },
  matchCase: { fontSize: '0.9rem', ml: 1 },
};

const pwdValidContainer: any = {
  lowerCase: /^(?=.*[a-z])/,
  upperCase: /^(?=.*[A-Z])/,
  numberChar: /^(?=.*[0-9])/,
  specialChar: /^(?=.*?[#?!@$%^&*-])/,
  minChars: /.{8,}$/,
};

interface PassworsValidTypes {
  id: number;
  label: string;
  value: string;
}

const passwordValidation: PassworsValidTypes[] = [
  { id: 1, label: 'Minimum of 8 characters', value: 'minChars' },
  { id: 2, label: 'At least one lowercase letter (a-z)', value: 'lowerCase' },
  { id: 3, label: 'At least one uppercase letter (a-z)', value: 'upperCase' },
  { id: 4, label: 'At least one number (0-9)', value: 'numberChar' },
  { id: 5, label: 'At least one special character (e.g. &?!:; etc.)', value: 'specialChar' },
];

const ChangePasswordForm = ({
  isOpen,
  passwordData,
  setPasswordData,
  handleClose = () => {},
  userEmail,
  isUpdateUserPasswordLoading,
  isUpdateUserPasswordError,
}: any) => {
  const themeData: any = get(useOutletContext(), 'themeData', {});
  const dispatch = useDispatch();

  const responsiveSize = useMediaQuery('(max-width:900px)');

  const [showPassword, setShowPassword] = useState<any>({});
  const [pwdMatchCases, setPwdMatchCases] = useState<any>({
    lowerCase: false,
    upperCase: false,
    numberChar: false,
    specialChar: false,
    minChars: false,
  });
  const [fieldErrors, setFieldErrors] = useState<any>({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  const togglePasswordVisibility = (name: any) => {
    setShowPassword((prevShowPassword: any) => ({
      ...prevShowPassword,
      [name]: !prevShowPassword[name],
    }));
  };

  useEffect(() => {
    if (isUpdateUserPasswordError) {
      setFieldErrors((pre: any) => ({ ...pre, currentPassword: true }));
    }
  }, [isUpdateUserPasswordError]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'newPassword') {
      Object.keys(pwdValidContainer).forEach((key: string) => {
        setPwdMatchCases((pre: any) => ({ ...pre, [key]: pwdValidContainer[key].test(value) }));
      });
    }
    if (name === 'confirmPassword') {
      setFieldErrors((pre: any) => ({ ...pre, confirmPassword: value !== passwordData['newPassword'] }));
    }
    setPasswordData((prevData: any) => ({
      ...prevData,
      [name]: showPassword[name] ? value : value.trim(),
    }));
  };

  const handleFormClose = () => {
    handleClose();
    setShowPassword({});
    setPwdMatchCases({
      lowerCase: false,
      upperCase: false,
      numberChar: false,
      specialChar: false,
      minChars: false,
    });
    setFieldErrors({
      currentPassword: false,
      newPassword: false,
      confirmPassword: false,
      newPasswordsMatched: true,
    });
    dispatch(userPasswordActions.resetInvalidPassword());
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const reqParams: any = {
      email: userEmail,
      current_password: passwordData.currentPassword,
      password: passwordData.newPassword,
    };
    dispatch(userPasswordActions.updateUserPasswordRequest(reqParams));
  };

  const isNewPWDValid = Object.keys(pwdMatchCases).every((key: string) => pwdMatchCases[key]);

  const renderFields = () => {
    const inputChangesOnValidation = (inputName: string) => {
      switch (inputName) {
        case 'currentPassword':
          return {
            helperText: fieldErrors[inputName] && 'Your current Password is wrong!',
            error: fieldErrors[inputName],
            sx: {
              margin: 0,
              '& .MuiOutlinedInput-root': {
                ...ChangePasswordStyles.defaultStyling,
              },
            },
          };
        case 'newPassword':
          return {
            error: fieldErrors.confirmPassword,
            sx: {
              margin: 0,
              '& .MuiOutlinedInput-root': {
                ...ChangePasswordStyles.defaultStyling,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor:
                    isNewPWDValid && passwordData.confirmPassword && !fieldErrors.confirmPassword ? 'palette.success.dark' : '',
                },
              },
            },
          };
        case 'confirmPassword':
          return {
            helperText: fieldErrors[inputName] && 'Passwords should be matched!',
            error: fieldErrors[inputName],
            sx: {
              margin: 0,
              '& .MuiOutlinedInput-root': {
                ...ChangePasswordStyles.defaultStyling,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: isNewPWDValid && passwordData[inputName] && !fieldErrors[inputName] ? 'palette.success.dark' : '',
                },
              },
            },
          };
        default:
          return {
            sx: {
              margin: 0,
              '& .MuiOutlinedInput-root': {
                ...ChangePasswordStyles.defaultStyling,
              },
            },
          };
      }
    };

    return (
      <Box sx={ChangePasswordStyles.modalWrapper}>
        {isUpdateUserPasswordLoading && (
          <Box sx={ChangePasswordStyles.loadingLayer}>
            <CircularProgress />
          </Box>
        )}
        <Grid
          container
          columnSpacing={1}
          rowSpacing={3}
          sx={ChangePasswordStyles.gridContainer}
          {...(isUpdateUserPasswordLoading && {
            onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
              e.preventDefault();
            },
          })}
        >
          {addPasswordUISchema.map(({ label, name }: any) => (
            <Fragment key={name}>
              <Grid
                xs={12}
                sm={12}
                md={4}
                item
                sx={[Styles.gridItem, { justifyContent: responsiveSize ? 'flex-start' : 'flex-end', pb: responsiveSize ? '4px': 0, pt: responsiveSize ?'24px':'36px !important'}]}
              >
                <Typography
                  sx={{...Styles.inputLabel, color: themeData.palette.text.primary, pb: fieldErrors[name] ? '20px' : '0' }}
                  className='required-field'
                >
                  {label}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={12} md={8} sx={{ '&.MuiGrid-item': { pt: responsiveSize ? 0 : 3 } }}>
                <TextField
                  name={name}
                  value={passwordData[name]}
                  type={showPassword[name] ? 'text' : 'password'}
                  margin='normal'
                  fullWidth
                  onChange={handleChange}
                  variant='outlined'
                  size='small'
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton
                          aria-label='toggle password visibility'
                          onClick={() => togglePasswordVisibility(name)}
                          edge='end'
                        >
                          {showPassword[name] ? <EyeIcon style={{ color: themeData.palette.custom.light }} /> : <EyeOffIcon style={{ color: themeData.palette.custom.light }} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  {...inputChangesOnValidation(name)}
                />
              </Grid>
            </Fragment>
          ))}
          <Grid item sm={12} md={4} sx={Styles.gridItem}></Grid>
          <Grid item sm={12} md={8}>
            <Typography sx={{...ChangePasswordStyles.pwdHeading, color: themeData.palette.text.primary}}>Password must contain:</Typography>
            {passwordValidation.map((option: PassworsValidTypes) => (
              <Box key={option.value} sx={ChangePasswordStyles.matchCasesWrapper}>
                {pwdMatchCases[option.value] ? <CheckIcon style={{ color : palette.success.dark}} /> : <CircleSmallIcon />}
                <Typography variant='subtitle1' sx={{...ChangePasswordStyles.matchCase, color: themeData.palette.text.primary}}>
                  {option.label}
                </Typography>
              </Box>
            ))}
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <FormModal
      title='CHANGE PASSWORD'
      isOpen={isOpen}
      handleSubmit={handleSubmit}
      handleClose={handleFormClose}
      disableSave={
        !passwordData.currentPassword.trim() ||
        !isNewPWDValid ||
        !(passwordData.newPassword.trim() && passwordData.newPassword === passwordData.confirmPassword)
      }
    >
      {renderFields()}
    </FormModal>
  );
};

export default ChangePasswordForm;
